import React from 'react';
import {
  AlertCircle, TrendingUp, Info, Lightbulb, CheckCircle2,
} from 'lucide-react';


const SEVERITY_META = {
  attention: {
    icon: AlertCircle,
    ring: 'border-amber-300',
    bg: 'bg-amber-50',
    dot: 'text-amber-700',
    label: 'Requiere atención',
  },
  positive: {
    icon: TrendingUp,
    ring: 'border-emerald-300',
    bg: 'bg-emerald-50',
    dot: 'text-emerald-700',
    label: 'Buena señal',
  },
  neutral: {
    icon: Info,
    ring: 'border-gray-200',
    bg: 'bg-gray-50',
    dot: 'text-gray-600',
    label: 'Observación',
  },
};


function InsightCard({ insight }) {
  const meta = SEVERITY_META[insight.severity] || SEVERITY_META.neutral;
  const Icon = meta.icon;
  return (
    <div
      className={`rounded-2xl border ${meta.ring} ${meta.bg} p-5`}
      data-testid={`insight-card-${insight.kind}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full bg-white grid place-items-center shrink-0 ${meta.dot}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <p className={`text-[10px] uppercase tracking-widest font-semibold ${meta.dot} mb-1`}>
            {meta.label}
          </p>
          <p
            className="font-serif text-base sm:text-lg text-habita-dark leading-snug mb-2"
            data-testid={`insight-observation-${insight.kind}`}
          >
            {insight.observation}
          </p>
          {insight.evidence?.length > 0 && (
            <ul
              className="text-xs text-gray-600 space-y-0.5 mb-3"
              data-testid={`insight-evidence-${insight.kind}`}
            >
              {insight.evidence.map((e, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400" />
                  {e}
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-gray-600 mb-2 italic" data-testid={`insight-interpretation-${insight.kind}`}>
            {insight.interpretation}
          </p>
          <p
            className="text-sm text-habita-dark flex items-start gap-1.5"
            data-testid={`insight-suggestion-${insight.kind}`}
          >
            <Lightbulb className="w-4 h-4 text-habita shrink-0 mt-0.5" />
            <span>{insight.suggestion}</span>
          </p>
        </div>
      </div>
    </div>
  );
}


export default function ProductInsightsSection({ insights, loading }) {
  if (loading && (!insights || insights.length === 0)) {
    return <div className="animate-pulse h-32 bg-gray-100 rounded-3xl" />;
  }
  const list = insights || [];
  return (
    <section className="mb-10" data-testid="section-insights">
      <h2 className="font-serif text-2xl text-habita-dark font-bold">
        Qué merece tu atención
      </h2>
      <p className="text-sm text-gray-500 mt-1 mb-5 max-w-2xl">
        Observaciones automáticas basadas en las métricas del rango — cada una incluye una posible interpretación y qué vale la pena investigar.
      </p>
      {list.length === 0 ? (
        <div
          className="rounded-2xl border border-gray-200 bg-white p-6 text-center"
          data-testid="insights-empty-state"
        >
          <CheckCircle2 className="w-6 h-6 text-emerald-600 inline-block mb-2" />
          <p className="text-sm text-gray-600">
            Todo se ve estable — no hay señales fuertes que requieran tu atención en este período.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="insights-grid">
          {list.map((i) => (
            <InsightCard key={i.id} insight={i} />
          ))}
        </div>
      )}
    </section>
  );
}
